import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { t } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Package, ShoppingBag, Users, Star, DollarSign, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, lang, formatPrice } = useStore();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const emptyProd = { name_ar: "", name_en: "", description_ar: "", description_en: "", price_usd: 0, category: "sweets", image: "", stock: 100, featured: false };
  const [form, setForm] = useState(emptyProd);

  const reload = async () => {
    const [s, p, o, c] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/products"),
      api.get("/orders"),
      api.get("/categories"),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCategories(c.data);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  if (!user) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to="/" />;

  const submitProduct = async () => {
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/products", form);
        toast.success("Created");
      }
      setOpen(false); setEditing(null); setForm(emptyProd);
      reload();
    } catch (e) { toast.error("Error"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف المنتج؟" : "Delete product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    reload();
  };

  const updateOrderStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    toast.success("Updated");
    reload();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">
      <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-4xl font-semibold mb-8`} style={{ color: "var(--thiqa-primary)" }}>
        {t(lang, "admin_dashboard")}
      </h1>
      <div className="flex gap-2 mb-8 border-b border-gray-200">
        {["dashboard", "products", "orders"].map((s) => (
          <button
            key={s}
            data-testid={`admin-tab-${s}`}
            onClick={() => setTab(s)}
            className={`px-5 py-3 text-sm font-medium ${tab === s ? "text-[color:var(--thiqa-primary)] border-b-2 border-[color:var(--thiqa-primary)]" : "text-gray-500"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <StatCard icon={Package} label={t(lang, "stats_products")} val={stats.total_products} />
          <StatCard icon={ShoppingBag} label={t(lang, "stats_orders")} val={stats.total_orders} />
          <StatCard icon={Users} label={t(lang, "stats_customers")} val={stats.total_users} />
          <StatCard icon={DollarSign} label={t(lang, "stats_revenue")} val={formatPrice(stats.revenue_usd)} />
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyProd); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-product-btn" className="btn-primary-thiqa rounded-full"><Plus size={16} className="me-1" />New</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Name (AR)</Label><Input value={form.name_ar} onChange={(e)=>setForm({...form, name_ar:e.target.value})} /></div>
                  <div><Label>Name (EN)</Label><Input value={form.name_en} onChange={(e)=>setForm({...form, name_en:e.target.value})} /></div>
                  <div className="md:col-span-2"><Label>Description (AR)</Label><Textarea value={form.description_ar} onChange={(e)=>setForm({...form, description_ar:e.target.value})} /></div>
                  <div className="md:col-span-2"><Label>Description (EN)</Label><Textarea value={form.description_en} onChange={(e)=>setForm({...form, description_en:e.target.value})} /></div>
                  <div><Label>Price USD</Label><Input type="number" step="0.01" value={form.price_usd} onChange={(e)=>setForm({...form, price_usd:parseFloat(e.target.value)||0})} /></div>
                  <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e)=>setForm({...form, stock:parseInt(e.target.value)||0})} /></div>
                  <div><Label>Category</Label>
                    <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="w-full h-10 border rounded-md px-3">
                      {categories.map((c)=><option key={c.id} value={c.id}>{c.name_en}</option>)}
                    </select>
                  </div>
                  <div><Label>Featured</Label><input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})} className="ms-2 h-4 w-4" /></div>
                  <div className="md:col-span-2"><Label>Image URL</Label><Input value={form.image} onChange={(e)=>setForm({...form, image:e.target.value})} /></div>
                </div>
                <Button data-testid="save-product-btn" onClick={submitProduct} className="btn-primary-thiqa rounded-full mt-4">Save</Button>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#e8ddc9] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#faf3e3]">
                <tr>
                  <th className="p-3 text-start">Image</th>
                  <th className="p-3 text-start">Name</th>
                  <th className="p-3 text-start">Category</th>
                  <th className="p-3 text-start">Price</th>
                  <th className="p-3 text-start">Stock</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100" data-testid={`admin-product-${p.id}`}>
                    <td className="p-3"><img src={p.image} className="w-12 h-12 object-cover rounded-lg" alt="" /></td>
                    <td className="p-3">{p.name_en}</td>
                    <td className="p-3">{p.category}</td>
                    <td className="p-3">${p.price_usd}</td>
                    <td className="p-3">{p.stock}</td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => { setEditing(p); setForm(p); setOpen(true); }} className="text-gray-600 hover:text-[color:var(--thiqa-primary)]"><Pencil size={16} /></button>
                      <button onClick={() => deleteProduct(p.id)} className="text-red-500"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-[#e8ddc9] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#faf3e3]">
              <tr>
                <th className="p-3 text-start">ID</th>
                <th className="p-3 text-start">Customer</th>
                <th className="p-3 text-start">Country</th>
                <th className="p-3 text-start">Payment</th>
                <th className="p-3 text-start">Total</th>
                <th className="p-3 text-start">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-gray-100" data-testid={`admin-order-${o.id}`}>
                  <td className="p-3 font-semibold" style={{ color: "var(--thiqa-primary)" }}>{o.id}</td>
                  <td className="p-3">{o.customer_name}</td>
                  <td className="p-3">{o.country}</td>
                  <td className="p-3">{o.payment_method}</td>
                  <td className="p-3">{formatPrice(o.total_usd)}</td>
                  <td className="p-3">
                    <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="border rounded px-2 py-1 text-xs">
                      {["pending","confirmed","shipped","delivered","cancelled"].map((s)=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, val }) => (
  <div className="bg-white border border-[#e8ddc9] rounded-2xl p-5">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-[#faf3e3] flex items-center justify-center">
        <Icon size={22} style={{ color: "var(--thiqa-primary)" }} />
      </div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-2xl font-bold" style={{ color: "var(--thiqa-primary)" }}>{val}</div>
      </div>
    </div>
  </div>
);
